import type { SyntheticEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { PartThumbnail } from '../../components/PartThumbnail'
import {
  BulbOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { apiClient } from '../../lib/api/client'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import type { CustomsInfo, CustomsLineInfo, OrderDetailData, OrderLine } from './domain'
import {
  CUSTOMS_CLASSIFICATION_DESCRIPTION,
  CUSTOMS_CLASSIFICATION_LABEL,
  resolveCustomsOriginState,
  resolveLineLabel,
} from './domain'

export interface CustomsPanelProps {
  readonly order: OrderDetailData
  /** Fired with the updated order once `PATCH /orders/{orderId}/customs`
   * returns 200 (UID-121 S7, ADR-0046). The panel is otherwise fully
   * controlled by `order` — the caller (OrderDetailPage) owns committing
   * the refreshed aggregate so every other order-detail surface (summary
   * card, activity log) re-renders from the same submitted state. */
  readonly onOrderChange?: (order: OrderDetailData) => void
}

/** Per-line customer-entered draft values, keyed by `orderLineId`. */
type DraftValues = Record<string, { partDescription: string; parentProductEndUse: string }>
type FieldErrors = Record<string, { partDescription?: string; parentProductEndUse?: string }>

/** The effective, client-derived state of the customs edit window
 * (UID-130 follow-up 48-hour rule), distinct from the raw wire
 * `submissionStatus`: `submitted` always wins; otherwise the window is
 * `closed` when the server already says so OR the 48-hour post-placement
 * grace period has elapsed; otherwise it's `open`. */
export type CustomsSubmissionState = 'open' | 'closed' | 'submitted'

const CUSTOMS_SUBMISSION_WINDOW_HOURS = 48

/**
 * Derives the effective customs submission state from the raw
 * `submissionStatus` plus the 48-hour post-placement edit window (UID-130
 * follow-up — "the customs edit window closes 48 hours after the order is
 * placed"). Kept as a small pure function so the 48-hour boundary math is
 * unit-testable without rendering anything.
 *
 * - `submitted` always wins (nothing left to edit).
 * - Otherwise `closed` when the server already marked the window closed,
 *   OR when `placedAt` is a valid instant at least 48h before `now`.
 * - A missing/unparsable `placedAt` is treated as NOT closed (fails open)
 *   rather than guessed at — we only close the window on evidence.
 */
export function resolveCustomsSubmissionState(
  submissionStatus: CustomsInfo['submissionStatus'],
  placedAt: string | null | undefined,
  now: Date = new Date(),
): CustomsSubmissionState {
  if (submissionStatus === 'submitted') return 'submitted'
  if (submissionStatus === 'closed') return 'closed'

  if (placedAt) {
    const placedTimeMs = new Date(placedAt).getTime()
    if (!Number.isNaN(placedTimeMs)) {
      const elapsedHours = (now.getTime() - placedTimeMs) / (1000 * 60 * 60)
      if (elapsedHours >= CUSTOMS_SUBMISSION_WINDOW_HOURS) return 'closed'
    }
  }

  return 'open'
}

function draftFromLines(lines: readonly CustomsLineInfo[]): DraftValues {
  const draft: DraftValues = {}
  for (const line of lines) {
    draft[line.orderLineId] = {
      partDescription: line.partDescription ?? '',
      parentProductEndUse: line.parentProductEndUse ?? '',
    }
  }
  return draft
}

function partLabel(orderLines: readonly OrderLine[], lineId: string): string {
  const orderLine = orderLines.find((candidate) => candidate.id === lineId)
  return orderLine ? resolveLineLabel(orderLine) : lineId
}

/** UID-411 — same join `partLabel` already does (`orderLineId` → `order.lines`), for the
 * per-part real thumbnail instead of the label. */
function partThumbnailUrl(
  orderLines: readonly OrderDetailData['lines'][number][],
  lineId: string,
): string | null {
  return orderLines.find((candidate) => candidate.id === lineId)?.thumbnailUrl ?? null
}

const CUSTOMS_THUMBNAIL_PLACEHOLDER = <span className="customs-part-thumbnail" aria-hidden="true" />

/**
 * Customs information tab panel (UX spec §2.2 item 4, §2.7; UID-121 S7;
 * UID-130 follow-up redesign). `order.customs` is real backend data (the
 * "order".order_customs + order_customs_line projection, ADR-0046),
 * reconciled against the prototype's 3-step vertical-stepper Customs tab
 * markup — the pixel source of truth here (no screenshot exists for this
 * screen):
 *
 * 1. Classification (Prototype/Production), 2. shipping terms — importer of
 * record (DDP) for an IMPORT order, or "Delivered At Place (DAP)" for a
 * domestic one (UID-232: keyed on country of origin via {@link isImportCustoms},
 * NOT on record presence), both read-only "done" steps with a teal check-circle.
 * 3. "Enter part description and end-use information below" — the only step with
 * real work left to do, marked with a filled numeral instead of a checkmark,
 * containing the per-part table.
 *
 * The 48-hour submission window (UID-130 follow-up) is derived client-side
 * via {@link resolveCustomsSubmissionState} from `submissionStatus` +
 * `order.placedAt` — `submitted` renders the same 3-column table read-only
 * with a submitted confirmation note; `closed` (by server status OR by age)
 * renders the orange "window has closed" banner above the stepper plus the
 * same read-only table; `open` renders the editable form.
 *
 * Fidelity notes vs. the prototype: the prototype's per-row checkboxes
 * ("apply info to multiple parts at once") are implemented here as a
 * client-side-only convenience — checking two-or-more rows and editing one
 * of them fans the edit out to the other checked rows; nothing about the
 * selection is persisted or sent to the server. The prototype's "Save and
 * finish later" button has no equivalent write endpoint yet (only
 * `submitCustoms` exists) and is intentionally rendered disabled rather
 * than guessed at.
 *
 * `order.customs === null`/`undefined` (no customs record) renders the
 * documented empty state rather than fabricating one.
 */
export function CustomsPanel({ order, onOrderChange }: CustomsPanelProps) {
  // UID-212 follow-up: the Customs tab ALWAYS shows the 3-step process (with or without the 48h
  // window banner) — never a bare "no record" empty state. When the order carries no persisted
  // customs record (a legacy order placed before UID-232, or one whose fail-open placement customs
  // write did not land), synthesize an OPEN, blank record from the order's lines so the editable
  // 3-step form still renders with one row per part. Display fallback only — the real record is
  // written at placement; submitting persists through the normal flow once it exists.
  const customs: CustomsInfo = order.customs ?? {
    classification: 'production',
    incoterms: 'DDP',
    importerOfRecord: null,
    submissionStatus: 'open',
    lines: order.lines.map((line) => ({
      orderLineId: line.id,
      partDescription: null,
      parentProductEndUse: null,
      hsCode: null,
      countryOfOrigin: null,
    })),
  }

  const state = resolveCustomsSubmissionState(customs.submissionStatus, order.placedAt)
  // UID-232 (code-review W1/W2): DAP-vs-DDP / importer-of-record is an IMPORT distinction keyed on
  // the parts' country of ORIGIN, NOT on record presence — every order now carries a record. Step 2
  // renders from the same shared origin state the summary card uses, so a domestic order never
  // claims "Fictiv is importer of record (DDP)" while the card reads "DAP" (W1). The `undetermined`
  // state (every live-placed order, incl. an overseas one) shows DAP as the default WITHOUT
  // asserting US origin — the order may still be an import (W2).
  const originState = resolveCustomsOriginState(customs)
  const isImport = originState === 'import'

  return (
    <div className="customs-panel">
      {state === 'closed' && (
        <output className="customs-window-banner">
          <div className="customs-window-banner-body">
            {/* Prototype (`Fictiv Platform.dc.html:3346`): a solid orange disc
            with a white "!", not the outline exclamation glyph. */}
            <span className="customs-window-banner-badge" aria-hidden="true">
              !
            </span>
            <div>
              <p className="customs-window-banner-title">
                Your customs submission window has closed
              </p>
              <p className="customs-window-banner-detail">
                The window to edit customs info has closed. Fictiv has attempted to complete the
                information on your behalf to avoid delays. Please reach out or send an email to{' '}
                <a href="mailto:support@fictiv.com">support@fictiv.com</a> to provide the necessary
                customs details. Changes are not possible after carrier pickup.
              </p>
            </div>
          </div>
          {/* UID-123 item 6: the design shows two links here alongside the
          mailto: (`Fictiv Platform.dc.html:3396-3397`), both decorative
          `cursor:pointer` spans with no wired handler in the prototype —
          same DECISION as ActionBar's Support button: a real, working
          `mailto:` rather than a link to nowhere or an invented route. */}
          <div className="customs-window-banner-links">
            <a
              href={`mailto:support@fictiv.com?subject=${encodeURIComponent(
                `Customs help — Order ${order.id}`,
              )}`}
              className="customs-window-banner-link"
            >
              <QuestionCircleOutlined aria-hidden="true" /> Contact us for help
            </a>
            <a
              href={`mailto:support@fictiv.com?subject=${encodeURIComponent(
                'International shipping — general question',
              )}`}
              className="customs-window-banner-link"
            >
              <BulbOutlined aria-hidden="true" /> Learn more about international shipping
            </a>
          </div>
        </output>
      )}
      {state === 'submitted' && (
        <p className="caption-text customs-submission-note">
          Customs information has been submitted for this order.
        </p>
      )}

      <Card className="customs-stepper-card">
        <ol className="customs-stepper">
          <li className="customs-step">
            <div className="customs-step-marker">
              <CheckCircleOutlined aria-hidden="true" className="customs-step-icon" />
              <span className="customs-step-connector" aria-hidden="true" />
            </div>
            <div className="customs-step-body">
              <p className="customs-step-heading">
                This order is classified as{' '}
                <span className="customs-step-emphasis">
                  {CUSTOMS_CLASSIFICATION_LABEL[customs.classification]}
                </span>
              </p>
              <p className="caption-text">
                {CUSTOMS_CLASSIFICATION_DESCRIPTION[customs.classification]}
              </p>
            </div>
          </li>
          <li className="customs-step">
            <div className="customs-step-marker">
              <CheckCircleOutlined aria-hidden="true" className="customs-step-icon" />
              <span className="customs-step-connector" aria-hidden="true" />
            </div>
            <div className="customs-step-body">
              {originState === 'import' && (
                <>
                  <p className="customs-step-heading">
                    Importing this order will be the responsibility of{' '}
                    <span className="customs-step-emphasis">
                      {customs.importerOfRecord ?? 'Fictiv'} ({customs.incoterms})
                    </span>
                  </p>
                  <p className="caption-text">
                    You&rsquo;ve requested Fictiv to be fully responsible for the shipping and
                    customs clearance of parts in your order.
                  </p>
                </>
              )}
              {originState === 'domestic' && (
                <>
                  <p className="customs-step-heading">
                    This order ships domestically under{' '}
                    <span className="customs-step-emphasis">Delivered At Place (DAP)</span>
                  </p>
                  <p className="caption-text">
                    Your parts originate in the United States, so there is no importer of record and
                    no customs duties for you to clear.
                  </p>
                </>
              )}
              {originState === 'undetermined' && (
                <>
                  <p className="customs-step-heading">
                    This order ships under{' '}
                    <span className="customs-step-emphasis">Delivered At Place (DAP)</span>
                  </p>
                  <p className="caption-text">
                    Your parts&rsquo; country of origin hasn&rsquo;t been confirmed yet, so DAP
                    applies by default. If any parts are imported, Fictiv will act as your importer
                    of record and handle customs clearance.
                  </p>
                </>
              )}
            </div>
          </li>
          <li className="customs-step customs-step-last">
            <div className="customs-step-marker">
              <span className="customs-step-icon customs-step-icon-filled" aria-hidden="true">
                3
              </span>
            </div>
            <div className="customs-step-body">
              <p className="customs-step-heading">
                Enter part description and end-use information below
              </p>
              <p className="caption-text">
                While the information you provide will not impact your order price, it does play a
                vital role in ensuring a seamless {isImport ? 'import ' : ''}experience. Fictiv uses
                these details, together with our logistics experts, to assign the correct HTS code
                on your behalf.{' '}
                {isImport
                  ? 'By supplying accurate information, you enable us to act as your importer and help prevent delays or compliance issues.'
                  : 'Supplying accurate information helps prevent delays or compliance issues.'}
              </p>
              {state === 'open' ? (
                <CustomsSubmitForm
                  orderId={order.id}
                  order={order}
                  lines={customs.lines ?? []}
                  onSubmitted={onOrderChange}
                />
              ) : state === 'closed' ? (
                // UID-317 review follow-up: once the 48h edit window has CLOSED (but nothing was
                // submitted), show the same form layout as `open` but fully DISABLED — inputs keep
                // their placeholders and the action buttons render disabled — so the user sees what
                // they would have filled in, now locked. `submitted` keeps its plain read-only table.
                <CustomsClosedForm order={order} lines={customs.lines ?? []} />
              ) : (
                <CustomsLineTable order={order} lines={customs.lines ?? []} />
              )}
            </div>
          </li>
        </ol>
      </Card>
    </div>
  )
}

function CustomsLineTable({
  order,
  lines,
}: {
  readonly order: Pick<OrderDetailData, 'lines'>
  readonly lines: readonly CustomsLineInfo[]
}) {
  if (lines.length === 0) {
    return <p className="caption-text">No per-part customs details are available yet.</p>
  }

  return (
    <div className="customs-parts-table-container">
      <div className="data-table-wrapper">
        <table className="data-table customs-line-table">
          <caption className="sr-only">Customs details by part</caption>
          <thead>
            <tr>
              <th scope="col">Part name</th>
              <th scope="col">Part description</th>
              <th scope="col">Parent product &amp; end-use</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const label = partLabel(order.lines, line.orderLineId)
              const thumbnailUrl = partThumbnailUrl(order.lines, line.orderLineId)
              return (
                <tr key={line.orderLineId}>
                  <td>
                    <div className="customs-part-cell">
                      <PartThumbnail
                        url={thumbnailUrl}
                        imgClassName="customs-part-thumbnail-img"
                        placeholder={CUSTOMS_THUMBNAIL_PLACEHOLDER}
                      />
                      {label}
                    </div>
                  </td>
                  <td>{line.partDescription ?? '—'}</td>
                  <td>{line.parentProductEndUse ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * UID-317 review follow-up: the CLOSED (48h edit window elapsed, nothing submitted) state. Mirrors
 * the editable `CustomsSubmitForm` layout — the same per-part table plus the two footer buttons —
 * but every field is a DISABLED input (keeping its placeholder guidance) and both buttons are
 * disabled, so the user sees the form they can no longer fill in. `submitted` uses the plain
 * read-only `CustomsLineTable` (showing the values the user entered); `open` uses the live form.
 */
function CustomsClosedForm({
  order,
  lines,
}: {
  readonly order: Pick<OrderDetailData, 'lines'>
  readonly lines: readonly CustomsLineInfo[]
}) {
  if (lines.length === 0) {
    return <p className="caption-text">No per-part customs details are available yet.</p>
  }

  const closedTitle = 'The customs edit window for this order has closed.'

  return (
    <div className="customs-parts-form">
      <div className="customs-parts-table-container">
        {/* Mirror the editable form's "Select all rows" + tip affordance, disabled. */}
        <div className="customs-parts-tip">
          <Button type="button" variant="outline" size="sm" disabled aria-disabled="true">
            Select all rows
          </Button>
          <p className="customs-parts-tip-text">
            <strong>Tip!</strong> Use the checkboxes to select and apply information to multiple
            parts at once
          </p>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table customs-line-table customs-submit-table">
            <caption className="sr-only">Customs details by part (edit window closed)</caption>
            <thead>
              <tr>
                <th scope="col">
                  <input type="checkbox" aria-label="Select all parts" disabled checked={false} />
                </th>
                <th scope="col">Part name</th>
                <th scope="col">
                  *Part Description<span className="sr-only"> (required)</span>
                  <InfoCircleOutlined aria-hidden="true" className="customs-column-info-icon" />
                </th>
                <th scope="col">
                  *Parent Product &amp; End-Use<span className="sr-only"> (required)</span>
                  <InfoCircleOutlined aria-hidden="true" className="customs-column-info-icon" />
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const label = partLabel(order.lines, line.orderLineId)
                const thumbnailUrl = partThumbnailUrl(order.lines, line.orderLineId)
                return (
                  <tr key={line.orderLineId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${label} to apply info to multiple parts`}
                        disabled
                        checked={false}
                      />
                    </td>
                    <td>
                      <div className="customs-part-cell">
                        <PartThumbnail
                          url={thumbnailUrl}
                          imgClassName="customs-part-thumbnail-img"
                          placeholder={CUSTOMS_THUMBNAIL_PLACEHOLDER}
                        />
                        {label}
                      </div>
                    </td>
                    <td>
                      <input
                        className="input-control"
                        placeholder="Ex. high voltage electronics insulator housing"
                        value={line.partDescription ?? ''}
                        disabled
                        readOnly
                        aria-label={`Part description for ${label}`}
                      />
                    </td>
                    <td>
                      <input
                        className="input-control"
                        placeholder="Ex. animatronic for theme park"
                        value={line.parentProductEndUse ?? ''}
                        disabled
                        readOnly
                        aria-label={`Parent product & end-use for ${label}`}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="customs-parts-footer">
        <Button type="button" variant="secondary" disabled aria-disabled="true" title={closedTitle}>
          Save and finish later
        </Button>
        <Button type="button" variant="primary" disabled aria-disabled="true" title={closedTitle}>
          Submit customs information
        </Button>
      </div>
    </div>
  )
}

function CustomsSubmitForm({
  orderId,
  order,
  lines,
  onSubmitted,
}: {
  readonly orderId: string
  readonly order: Pick<OrderDetailData, 'lines'>
  readonly lines: readonly CustomsLineInfo[]
  readonly onSubmitted?: ((order: OrderDetailData) => void) | undefined
}) {
  const [values, setValues] = useState<DraftValues>(() => draftFromLines(lines))
  // UID-610 — `useRangeSelection` carries the Gmail/Finder shift-click anchor
  // alongside the selection this panel's "apply to multiple parts" fan-out
  // already used.
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const errorBannerRef = useRef<HTMLDivElement | null>(null)

  // Re-seed the draft whenever the underlying lines identity changes (a
  // fresh order load) — never on every render, or an in-progress edit would
  // be clobbered by its own re-render.
  useEffect(() => {
    setValues(draftFromLines(lines))
  }, [lines])

  useEffect(() => {
    if (submitError) errorBannerRef.current?.focus()
  }, [submitError])

  if (lines.length === 0) {
    return <p className="caption-text">No per-part customs details are available yet.</p>
  }

  /** UID-610 — shift-click range selection over this table's rendered row
   * order (Gmail/Finder semantics, same as UID-470's Parts Library).
   * `shiftKey` reaches here from the row checkbox's `onClick`, not
   * `onChange` (a `ChangeEvent` carries no modifier-key state). */
  function toggleSelected(lineId: string, shiftKey: boolean) {
    rangeSelection.toggle(
      lineId,
      lines.map((l) => l.orderLineId),
      shiftKey,
    )
  }

  function toggleSelectAll() {
    rangeSelection.toggleAll(
      lines.map((l) => l.orderLineId),
      selectedIds.size !== lines.length,
    )
  }

  function handleFieldChange(
    lineId: string,
    field: 'partDescription' | 'parentProductEndUse',
    value: string,
  ) {
    // Prototype "apply information to multiple parts at once" affordance:
    // editing a field on a row that's part of a multi-row selection fans
    // the value out to every other checked row (client-side only, never
    // persisted as its own state).
    const targets =
      selectedIds.has(lineId) && selectedIds.size > 1 ? selectedIds : new Set([lineId])
    setValues((current) => {
      const next = { ...current }
      for (const id of targets) {
        next[id] = {
          ...(next[id] ?? { partDescription: '', parentProductEndUse: '' }),
          [field]: value,
        }
      }
      return next
    })
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()

    const errors: FieldErrors = {}
    for (const line of lines) {
      const draft = values[line.orderLineId] ?? { partDescription: '', parentProductEndUse: '' }
      const lineErrors: FieldErrors[string] = {}
      if (!draft.partDescription.trim())
        lineErrors.partDescription = 'Part description is required.'
      if (!draft.parentProductEndUse.trim())
        lineErrors.parentProductEndUse = 'Parent product & end-use is required.'
      if (lineErrors.partDescription || lineErrors.parentProductEndUse) {
        errors[line.orderLineId] = lineErrors
      }
    }

    setFieldErrors(errors)
    setSubmitError(undefined)

    if (Object.keys(errors).length > 0) {
      const firstInvalidLine = lines.find((line) => errors[line.orderLineId])
      if (firstInvalidLine) {
        const field = errors[firstInvalidLine.orderLineId]?.partDescription
          ? 'partDescription'
          : 'parentProductEndUse'
        inputRefs.current[`${firstInvalidLine.orderLineId}-${field}`]?.focus()
      }
      return
    }

    setSubmitting(true)
    try {
      const { data, error } = await apiClient.PATCH('/orders/{orderId}/customs', {
        params: { path: { orderId } },
        body: {
          lines: lines.map((line) => {
            const draft = values[line.orderLineId] ?? {
              partDescription: '',
              parentProductEndUse: '',
            }
            return {
              orderLineId: line.orderLineId,
              partDescription: draft.partDescription.trim(),
              parentProductEndUse: draft.parentProductEndUse.trim(),
            }
          }),
        },
      })

      if (data) {
        onSubmitted?.(data)
        return
      }

      const status = error?.status
      if (status === 409) {
        const type = error?.type
        setSubmitError(
          type === 'customs/already_submitted'
            ? 'This order’s customs information has already been submitted.'
            : 'Your customs submission window has closed. Refresh the page to see the latest status.',
        )
      } else if (status === 400) {
        setSubmitError(
          error?.detail ?? 'We couldn’t submit that — please check the fields and try again.',
        )
      } else if (status === 404) {
        setSubmitError('We couldn’t find this order. Please refresh and try again.')
      } else {
        setSubmitError('Something went wrong submitting customs information. Please try again.')
      }
    } catch {
      setSubmitError('Something went wrong submitting customs information. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const allSelected = selectedIds.size > 0 && selectedIds.size === lines.length

  return (
    <form className="customs-submit-form" onSubmit={handleSubmit} noValidate>
      {submitError && (
        <div
          role="alert"
          className="customs-submit-error-banner"
          ref={errorBannerRef}
          tabIndex={-1}
        >
          {submitError}
        </div>
      )}
      <div className="customs-parts-table-container">
        <div className="customs-parts-tip">
          <Button type="button" variant="outline" size="sm" onClick={toggleSelectAll}>
            Select all rows
          </Button>
          <p className="customs-parts-tip-text">
            <strong>Tip!</strong> Use the checkboxes to select and apply information to multiple
            parts at once
          </p>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table customs-line-table customs-submit-table">
            <caption className="sr-only">Enter part description and end-use information</caption>
            <thead>
              <tr>
                <th scope="col">
                  <input
                    type="checkbox"
                    aria-label="Select all parts"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th scope="col">Part name</th>
                <th scope="col">
                  *Part Description<span className="sr-only"> (required)</span>
                  <InfoCircleOutlined aria-hidden="true" className="customs-column-info-icon" />
                </th>
                <th scope="col">
                  *Parent Product &amp; End-Use<span className="sr-only"> (required)</span>
                  <InfoCircleOutlined aria-hidden="true" className="customs-column-info-icon" />
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const draft = values[line.orderLineId] ?? {
                  partDescription: '',
                  parentProductEndUse: '',
                }
                const errors = fieldErrors[line.orderLineId]
                const label = partLabel(order.lines, line.orderLineId)
                const thumbnailUrl = partThumbnailUrl(order.lines, line.orderLineId)
                const descId = `customs-${line.orderLineId}-part-description`
                const endUseId = `customs-${line.orderLineId}-parent-product`

                return (
                  <tr key={line.orderLineId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${label} to apply info to multiple parts`}
                        checked={selectedIds.has(line.orderLineId)}
                        // UID-610 — reads `event.shiftKey` off `onClick`
                        // (mouse AND the Shift+Space keyboard equivalent —
                        // see `PartsTable.tsx`'s precedent); `onChange`
                        // stays a no-op since a `ChangeEvent` has no
                        // modifier-key fields.
                        onClick={(event) => toggleSelected(line.orderLineId, event.shiftKey)}
                        onChange={() => {}}
                      />
                    </td>
                    <td>
                      <div className="customs-part-cell">
                        <PartThumbnail
                          url={thumbnailUrl}
                          imgClassName="customs-part-thumbnail-img"
                          placeholder={CUSTOMS_THUMBNAIL_PLACEHOLDER}
                        />
                        {label}
                      </div>
                    </td>
                    <td>
                      <label htmlFor={descId} className="sr-only">
                        Part description for {label}
                      </label>
                      <input
                        id={descId}
                        ref={(node) => {
                          inputRefs.current[`${line.orderLineId}-partDescription`] = node
                        }}
                        className={[
                          'input-control',
                          errors?.partDescription && 'input-control-error',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        placeholder="Ex. high voltage electronics insulator housing"
                        value={draft.partDescription}
                        required
                        aria-required="true"
                        aria-invalid={errors?.partDescription ? true : undefined}
                        aria-describedby={errors?.partDescription ? `${descId}-error` : undefined}
                        onChange={(event) =>
                          handleFieldChange(line.orderLineId, 'partDescription', event.target.value)
                        }
                      />
                      {errors?.partDescription && (
                        <p id={`${descId}-error`} className="input-error-text">
                          {errors.partDescription}
                        </p>
                      )}
                    </td>
                    <td>
                      <label htmlFor={endUseId} className="sr-only">
                        Parent product &amp; end-use for {label}
                      </label>
                      <input
                        id={endUseId}
                        ref={(node) => {
                          inputRefs.current[`${line.orderLineId}-parentProductEndUse`] = node
                        }}
                        className={[
                          'input-control',
                          errors?.parentProductEndUse && 'input-control-error',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        placeholder="Ex. animatronic for theme park"
                        value={draft.parentProductEndUse}
                        required
                        aria-required="true"
                        aria-invalid={errors?.parentProductEndUse ? true : undefined}
                        aria-describedby={
                          errors?.parentProductEndUse ? `${endUseId}-error` : undefined
                        }
                        onChange={(event) =>
                          handleFieldChange(
                            line.orderLineId,
                            'parentProductEndUse',
                            event.target.value,
                          )
                        }
                      />
                      {errors?.parentProductEndUse && (
                        <p id={`${endUseId}-error`} className="input-error-text">
                          {errors.parentProductEndUse}
                        </p>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="customs-parts-footer">
          {/* The prototype shows a "Save and finish later" button, but no
          write endpoint for a draft/partial save exists in the API contract
          (only `submitCustoms`) — rendered visually per the design but
          disabled with an explicit reason rather than wired to an invented
          endpoint (see the class doc comment). */}
          <Button
            type="button"
            variant="secondary"
            disabled
            aria-disabled="true"
            title="Saving and finishing later isn't available yet — please submit when your information is ready."
          >
            Save and finish later
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Submit customs information
          </Button>
        </div>
      </div>
    </form>
  )
}
